import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import 'dayjs/locale/de-at';
import './styles/fonts/inter/400.css';
import './styles/fonts/inter/500.css';
import './styles/fonts/inter/600.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import { App as AntApp, Button, ConfigProvider, FloatButton, Layout, Result, Spin } from 'antd';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import 'antd/dist/reset.css';
import './styles/global.css';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { AuthLoadingScreen } from '@medcom/shared-ui/components/AuthLoadingScreen.tsx';
import { SessionExpired } from '@medcom/shared-ui/components/SessionExpired.tsx';
import { currentUrlWithoutAuthParams, isSessionExpiredPage, sessionExpiredPath } from '@medcom/shared-ui/utils/oidc.ts';
import { reportError, reportRenderError, setTelemetryUser } from '@medcom/shared-ui/utils/telemetry.ts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuth } from 'react-oidc-context';
import { getTheme } from './api/theme.queries.ts';
import { getUserInfo } from './api/user-info.queries.ts';
import { AppHeader } from './components/AppHeader';
import { AppVersionAutoUpdater } from './components/AppVersionAutoUpdater.tsx';
import { AppVersionCheckerModal } from './components/AppVersionCheckerModal';
import { ErrorFallback } from './components/ErrorFallback';
import { ImportFileWatcher } from './components/ImportFileWatcher.tsx';
import { LoadingIndicator } from './components/LoadingIndicator';
import { NoAccess } from './components/NoAccess';
import { OfflineBlockingModal } from './components/OfflineBlockingModal';
import { PrivateRoute } from './components/PrivateRoute';
import { TopMenu } from './components/TopMenu.tsx';
import { DynamicConfigProvider } from './DynamicConfigProvider.tsx';
import { useAuthEvents } from './hooks/useAuthEvents';
import { SocketProvider } from './socket/SocketProvider.tsx';
import { defaultTheme, useThemeStore } from './store/useThemeStore.ts';
import { UserMenuWrapper } from './UserMenuWrapper.tsx';
import { SCROLLER } from './utils/elementIds';
import { Role } from './utils/user';
import { Accounts } from './views/admin/Accounts.tsx';
import { Themes } from './views/admin/Themes.tsx';
import { Users } from './views/admin/Users.tsx';
import { Befunde } from './views/befunde/Befunde.tsx';

const { Content } = Layout;

dayjs.locale('de-at');
// plugins needed for production build
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

Spin.setDefaultIndicator(<LoadingOutlined />);

const queryClient = new QueryClient();

const useAppSyles = () => {
  return useMemo(
    () => ({
      layout: css`
        min-height: 100dvh;
      `,
      mainContent: css`
        height: calc(100dvh - 80px);

        .ant-layout-content,
        .ant-layout-sider-children {
          overflow-y: auto;
        }
      `,
    }),
    []
  );
};

const App: React.FC = () => {
  const auth = useAuth();
  const [appInitialized, setAppInitialized] = useState(false);
  const { setTheme } = useThemeStore();

  useEffect(() => {
    if (auth.error) {
      reportError(auth.error, { source: 'oidc', url: currentUrlWithoutAuthParams() });
    }
  }, [auth.error]);

  useEffect(() => {
    if (!auth.isAuthenticated || appInitialized) {
      return;
    }

    const init = async () => {
      setTelemetryUser({
        id: auth.user?.profile.sub,
        username: auth.user?.profile.preferred_username,
        email: auth.user?.profile.email,
      });

      try {
        const userInfo = await getUserInfo(queryClient);
        if (userInfo?.themeId) {
          const theme = await getTheme(queryClient, { id: userInfo.themeId });
          setTheme(theme, false);
        }
      } catch (e) {
        console.error('Failed to initialize app data:', e);
      }

      setAppInitialized(true);
    };

    init();
  }, [auth, appInitialized, setTheme]);

  const styles = useAppSyles();

  // while useAuthEvents retries a failed callback, auth.error is still set — don't
  // flash the error screen before the redirect kicks in
  if (!appInitialized && auth.error && !auth.activeNavigator) {
    return (
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: defaultTheme.colorPrimary,
          },
        }}
      >
        <Result
          status={500}
          title="Authentifizierung fehlgeschlagen"
          subTitle={auth.error.message}
          extra={
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                window.location.href = '/';
              }}
              type="primary"
            >
              Applikation neu laden
            </Button>
          }
        />
      </ConfigProvider>
    );
  }

  // Not authenticated: either the auto sign-in redirect is in flight, or it got
  // stuck (iOS standalone PWA opens Keycloak in a separate in-app browser sheet, so
  // the webview never navigates). Show the loading/retry screen, not the main app
  // tree — otherwise the plain LoadingIndicator below would trap the user with no
  // way back to the Keycloak dialog.
  if (auth.isLoading || !auth.isAuthenticated) {
    return (
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: defaultTheme.colorPrimary,
          },
        }}
      >
        <AuthLoadingScreen />
      </ConfigProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <DynamicConfigProvider>
        <AntApp>
          <ErrorBoundary FallbackComponent={ErrorFallback} onError={reportRenderError}>
            {!appInitialized && <LoadingIndicator height="100dvh" />}
            {appInitialized && (
              <SocketProvider>
                <BrowserRouter>
                  <Layout className={styles.layout}>
                    <AppHeader userActions={<UserMenuWrapper />} topMenu={<TopMenu />} />
                    <Layout className={styles.mainContent}>
                      <Content id={SCROLLER}>
                        <Routes>
                          <Route element={<PrivateRoute />}>
                            <Route path="/:id?" element={<Befunde />} />
                          </Route>
                          <Route element={<PrivateRoute roles={[Role.ROLE_BEF_MEDCOM]} />}>
                            <Route path="/admin/accounts" element={<Accounts />} />
                            <Route path="/admin/benutzer" element={<Users />} />
                            <Route path="/admin/themes" element={<Themes />} />
                          </Route>
                          <Route path="/no-access" element={<NoAccess />} />
                          <Route path="*" element={<Navigate replace to="/" />} />
                        </Routes>
                      </Content>
                      <FloatButton.BackTop target={() => document.getElementById(SCROLLER) as HTMLElement} visibilityHeight={2500} />
                      <AppVersionCheckerModal />
                      <AppVersionAutoUpdater />
                      <OfflineBlockingModal />
                      <ImportFileWatcher />
                      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
                    </Layout>
                  </Layout>
                </BrowserRouter>
              </SocketProvider>
            )}
          </ErrorBoundary>
        </AntApp>
      </DynamicConfigProvider>
    </QueryClientProvider>
  );
};

// The session-expired page must not sign in by itself, the user decides when
export const AppRoot: React.FC = () => {
  const sessionExpired = isSessionExpiredPage();
  useAuthEvents({ autoSignin: !sessionExpired, sessionEndedPath: sessionExpiredPath });

  return sessionExpired ? (
    <ConfigProvider theme={{ token: { colorPrimary: defaultTheme.colorPrimary } }}>
      <SessionExpired />
    </ConfigProvider>
  ) : (
    <App />
  );
};
